import Client, { Options } from '@/api/client'
import { getConfig } from '@/app/config'
type HttpMethod = keyof Pick<Client, 'get' | 'post' | 'put' | 'patch' | 'delete' | 'getFile' | 'postFile' | 'putFile'>
export type EndpointConfig<TParams = unknown, TResponse = unknown, TMapped = TResponse> = {
  requestConfig: (params: TParams) => {
    method: HttpMethod
    endpoint: string
    body?: object
    file?: File
    options?: Options
  } & (TResponse extends void
    ? {
        // Handles the POST/PUT/DELETE cases especially where we usually don't need to deserialize the response
        deserializationStrategy?: never
      }
    : {
        /**
         * accepts a zod schema or a function that takes the response and returns the mapped object
         *
         * Might want to switch to using parseAsync by default, but just using parse for now until we see async transforms or refines
         */
        deserializationStrategy:
          | { parse: (data: TResponse) => TMapped }
          | ((response: TResponse) => Promise<TMapped> | TMapped)
      })
}

export type ApiConfig<
  TConfig extends {
    // Needs to use `any` to be able to infer types correctly
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof TConfig]: EndpointConfig<any, any, any>
  },
> = {
  [K in keyof TConfig]: TConfig[K]
}

// Helper type to extract generic parameters
type ExtractEndpointConfigParams<T> =
  T extends EndpointConfig<infer TParams, infer TResponse, infer TMapped> ? [TParams, TResponse, TMapped] : never

export const clientFactory = <
  TConfig extends {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof TConfig]: EndpointConfig<any, any, any>
  },
  K extends keyof TConfig,
>(
  config: ApiConfig<TConfig>,
  client: Client = new Client(getConfig().API_ENDPOINT ?? '')
): {
  [K in keyof TConfig]: (
    params: Parameters<TConfig[K]['requestConfig']>[0]
  ) => Promise<ExtractEndpointConfigParams<TConfig[K]>[2]>
} => {
  const clientMethods: Partial<{
    [K in keyof TConfig]: (
      params: Parameters<TConfig[K]['requestConfig']>[0]
    ) => Promise<ExtractEndpointConfigParams<TConfig[K]>[2]>
  }> = {}

  for (const key in config) {
    if (Object.prototype.hasOwnProperty.call(config, key)) {
      clientMethods[key] = async (params) => {
        const { method, endpoint, body, file, options, deserializationStrategy } = config[key].requestConfig(params)
        let response

        switch (method) {
          case 'post':
            response = await client.post<ExtractEndpointConfigParams<TConfig[K]>[1]>(endpoint, body, options)
            break
          case 'put':
            response = await client.put<ExtractEndpointConfigParams<TConfig[K]>[1]>(endpoint, body, options)
            break
          case 'patch':
            response = await client.patch<ExtractEndpointConfigParams<TConfig[K]>[1]>(endpoint, body, options)
            break
          case 'delete':
            response = await client.delete<ExtractEndpointConfigParams<TConfig[K]>[1]>(endpoint, body, options)
            break
          case 'getFile':
            response = await client.getFile(endpoint, options)
            break
          case 'postFile':
            if (!file) throw new Error('File is required for postFile method')
            response = await client.postFile<ExtractEndpointConfigParams<TConfig[K]>[1]>(endpoint, file, body, options)
            break
          case 'putFile':
            if (!file) throw new Error('File is required for putFile method')
            response = await client.putFile<ExtractEndpointConfigParams<TConfig[K]>[1]>(endpoint, file, body, options)
            break
          case 'get':
            response = await client.get<ExtractEndpointConfigParams<TConfig[K]>[1]>(endpoint, options)
            break
          default:
            throw new Error(`Unsupported method: ${method}`)
        }
        if (deserializationStrategy) {
          if ('parse' in deserializationStrategy) {
            return deserializationStrategy.parse(response)
          }
          return deserializationStrategy(response)
        }
        return response
      }
    }
  }

  return clientMethods as {
    [K in keyof TConfig]: (
      params: Parameters<TConfig[K]['requestConfig']>[0]
    ) => Promise<ExtractEndpointConfigParams<TConfig[K]>[2]>
  }
}
